import {BaseAlert, BaseButton, BaseCard} from "@/components";
import type {Article} from "./news.types";
import {useNews} from "./useNews";

const formatDate = (value: string): string => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("es-VE", {day: "2-digit", month: "short", year: "numeric"});
};

export interface NewsListPageProps {
  onSelectArticle?: (article: Article) => void;
}

const NewsListPage = ({onSelectArticle}: NewsListPageProps) => {
  const {articles, isLoading, isError, error, refetch} = useNews();

  return (
    <section className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-8">
      <header className="flex flex-col gap-2">
        <h1 className="text-[var(--font-h1)] font-bold text-[var(--color-text)]">Noticias</h1>
        <p className="text-[var(--font-body-lg)] text-[var(--color-text-secondary)]">
          Entérate de las últimas novedades.
        </p>
      </header>

      {isLoading ? (
        <BaseCard
          variant="surface"
          padding="lg"
        >
          <p className="text-center text-[var(--font-body-lg)] text-[var(--color-text-secondary)]">
            Cargando artículos...
          </p>
        </BaseCard>
      ) : isError ? (
        <BaseAlert
          variant="danger"
          title="No pudimos cargar las noticias"
        >
          <p>{error?.message ?? "Inténtalo nuevamente."}</p>
          <BaseButton
            variant="secondary"
            size="sm"
            onClick={() => refetch()}
          >
            Reintentar
          </BaseButton>
        </BaseAlert>
      ) : articles.length === 0 ? (
        <BaseCard
          variant="surface"
          padding="lg"
        >
          <p className="text-center text-[var(--font-body-lg)] text-[var(--color-text-secondary)]">
            No hay noticias publicadas por ahora.
          </p>
        </BaseCard>
      ) : (
        <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {articles.map(article => (
            <li key={article.id}>
              <BaseCard
                variant="surface"
                padding="none"
                interactive
                onClick={() => onSelectArticle?.(article)}
              >
                {article.image ? (
                  <img
                    src={article.image}
                    alt={article.title}
                    className="h-44 w-full rounded-t-xl object-cover"
                    loading="lazy"
                  />
                ) : null}
                <div className="flex flex-col gap-2 p-4">
                  <h2 className="text-[var(--font-h3)] font-semibold text-[var(--color-text)]">{article.title}</h2>
                  {article.excerpt ? (
                    <p className="line-clamp-3 text-[var(--font-body)] text-[var(--color-text-secondary)]">
                      {article.excerpt}
                    </p>
                  ) : null}
                  {article.createdAt ? (
                    <p className="text-[var(--font-body)] text-[var(--color-text-tertiary)]">
                      {formatDate(article.createdAt)}
                    </p>
                  ) : null}
                </div>
              </BaseCard>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default NewsListPage;
